import { BaseFlexSeoResponse } from "./BaseFlexSeoResponse";
import { Composition, ViewModelResponse } from "typings/flexFramework/FlexDefinitions";
import { findFlexComponent } from "src/stores/CompositionStore/CompositionStore";
import { PageStore } from "bernie-core";
import { NamedMeta, PaginationAttribute, PropertyMeta } from "bernie-http";
import {
  optimizeHeroImageUrl,
  optimizeHotwireHeroImageUrl,
} from "src/components/flexComponents/DestinationHero/WizardHeroUtils";

/**
 * Implementation of a BaseFlexSeo that represents a ViewModelResponse's SEO Metadata
 * with a composition.
 */
export class FlexSeoWithCompositionResponse extends BaseFlexSeoResponse implements ViewModelResponse {
  public composition: Composition;

  public hrefNextPage: string;

  public hrefPreviousPage: string;

  public constructor(viewModelResponse: ViewModelResponse) {
    super(viewModelResponse);
    this.composition = viewModelResponse.composition;
    this.hrefNextPage = viewModelResponse.hrefNextPage;
    this.hrefPreviousPage = viewModelResponse.hrefPreviousPage;
  }

  public hasEnoughData = (): boolean => this.composition && super.hasEnoughData();

  public getTemplateId = (): string =>
    `${this.flexContext.siteContext.id}-${this.getResolvedLocale()}-${this.composition.templateName}`;

  public getDataFmSeo = (): string => this.composition.meta["data-fm-seo"];

  public getDescription = (): string => this.composition.meta["description"];

  public getKeywords = (): string => this.composition.meta["keywords"];

  public getOgImage = (): string => this.getHeroImageUrl();

  public getTemplateFmId = (): string => `${this.composition.templateId}`;

  public getTemplateVersion = (): string => `${this.composition.templateVersion}`;

  public getTitle = (): string => this.composition.title;

  public getRobots = (): string => this.composition.meta["robots"];

  /**
   * Dives in composition to find the hero image url.
   */
  private getHeroImageUrl(): string {
    const isHotwire = this.flexContext?.siteContext?.brandId === "hotwire";

    const imageUrl = isHotwire ? this.getHotwireHeroImageUrl() : this.getExpediaHeroImageUrl();

    if (!this.flexContext?.userContext?.deviceInformation) {
      return imageUrl;
    }

    return isHotwire
      ? optimizeHotwireHeroImageUrl(imageUrl, this.flexContext.userContext.deviceInformation.mobile)
      : optimizeHeroImageUrl(imageUrl, this.flexContext.userContext.deviceInformation.mobile);
  }

  private getExpediaHeroImageUrl(): string {
    const wizardRegion = findFlexComponent(this.composition.page.regionList, (c) => c.name === "wizard");
    const destinationWizard = wizardRegion?.modules?.find((module) => module.name === "DestinationWizard");
    const wizardHero = destinationWizard?.modules?.find((module) => module.name === "destination-hero");
    const mediaUrl = wizardHero?.model?.content?.media?.[0]?.mediaUrl;
    return this.parseMediaUrl(mediaUrl);
  }

  private getHotwireHeroImageUrl(): string {
    const hotwireWizardRegion = findFlexComponent(this.composition.page.regionList, (c) => c.name === "HotwireWizard");

    // hotwire's composition on desktop/mobile landing pages
    const hotwireDestinationHero = hotwireWizardRegion?.modules?.find((module) => module.name === "destination-hero");
    if (hotwireDestinationHero) {
      const mediaUrl = hotwireDestinationHero.model?.content?.media?.[0]?.mediaUrl;
      return this.parseMediaUrl(mediaUrl);
    }

    // hotwire's composition on desktop/mobile index pages
    return this.flexContext?.userContext?.deviceInformation?.mobile
      ? hotwireWizardRegion?.model?.smallBackgroundImage
      : hotwireWizardRegion?.model?.backgroundImage;
  }

  // Override SEO meta data when in template rendering path.
  public overridePageStore(pageStore: PageStore) {
    if (!this.composition) {
      return;
    }

    pageStore.title = this.getTitle() || pageStore.title;
    pageStore.seo = {
      ...pageStore.seo,
      description: this.getDescription() || pageStore.seo.description,
      additionalMeta: pageStore.seo.additionalMeta,
    };

    if (this.getKeywords()) {
      pageStore.seo?.additionalMeta?.push(new NamedMeta("keywords", this.getKeywords()));
    }
    if (this.getDataFmSeo()) {
      pageStore.seo?.additionalMeta?.push(new NamedMeta("data-fm-seo", this.getDataFmSeo()));
    }
    if (this.getOgImage()) {
      pageStore.seo?.additionalMeta?.push(new PropertyMeta("og:image", this.getDataFmSeo()));
    }
    if (this.getDescription()) {
      pageStore.seo?.additionalMeta?.push(new PropertyMeta("og:description", this.getDescription()));
    }
    if (this.getTitle()) {
      pageStore.seo?.additionalMeta?.push(new PropertyMeta("og:title", this.getTitle()));
    }
    if (this.getOgImage()) {
      pageStore.seo?.additionalMeta?.push(new PropertyMeta("og:image", this.getDataFmSeo()));
    }
    if (this.hrefNextPage) {
      pageStore.seo?.additionalMeta?.push(new PaginationAttribute("next", this.hrefNextPage));
    }
    if (this.hrefPreviousPage) {
      pageStore.seo?.additionalMeta?.push(new PaginationAttribute("prev", this.hrefPreviousPage));
    }
  }
}
